/**
 * New Customer Intake page.
 *
 * Single-page intake form for the office. Captures the customer +
 * primary contact + first service address in one POST. Backed by
 * POST /api/customers/intake (see src/server/routes/crm.ts).
 *
 * Sections:
 *   1. About this customer  (company / type / how they heard about us / referrer)
 *   2. Primary contact      (name + at-least-one-channel)
 *   3. Service address      (line1 / city / state / zip / water provider)
 *   4. Notes                (initial call notes / work request)
 *
 * Failure modes:
 *   - 409 DUPLICATE_CUSTOMER → conflict panel above the form, no nav.
 *   - 400 VALIDATION         → inline field errors + form-level banner.
 *   - 401/403 / network      → toast.
 *
 * Success → toast + redirect straight to the customer detail page,
 * passing an `intakeHandoff` location state so the detail page can
 * render an inline next-step banner (queue-first-test, similar-customer
 * warning, and prominent New Job / New Estimate CTAs). Skipping the
 * intermediate success panel saves a click for the most common follow-up.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BackLink } from "@/components/back-link";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertCircle, ArrowLeft, Building2, ExternalLink, Loader2, MapPin, Save, Search, User, X } from "lucide-react";

import { api } from "@/lib/api";
import { HomeOrBusinessButtons, type CustomerType } from "@/components/customer-home-or-business";
import { openExternalLink } from "@/lib/standalone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { blankToUndefined, formatPhoneOnBlur, parseIntakeError, type IntakeConflict, US_STATES } from "./intakeHelpers";
import { HEARD_ABOUT_OPTIONS, heardAboutDetailPrompt, type HeardAbout } from "@/lib/cameFrom";

// ────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────

interface IntakeSuccess {
    customer_id: string;
    contact_id: string;
    site_id: string;
    similar: Array<{
        id: string;
        company_name: string;
        match_reason: "name_substring" | "same_zip_similar_address" | "phone_partial";
    }>;
}

interface CustomerSearchRow {
    id: string;
    company_name: string;
    customer_type: string;
    is_active: boolean;
}

interface WaterProvider {
    id: string;
    name: string;
    display_name?: string | null;
    report_name?: string | null;
    parent_water_provider_id?: string | null;
    slug: string;
    is_active: boolean;
    submission_platform: string | null;
    portal_url: string | null;
    is_portal?: boolean | null;
}

// ────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────

export default function CustomerIntakePage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // ── Form state ────────────────────────────────────────────────────
    const [companyName, setCompanyName] = useState("");
    // No default: the office must pick Home or Business (Corbin 2026-10-28).
    const [customerType, setCustomerType] = useState<CustomerType | null>(null);
    const [typeError, setTypeError] = useState<string | null>(null);
    // "How did they hear about us?" (migration 711). No default on purpose:
    // a pre-picked answer is the one nobody changes, and then every customer
    // "came from a phone call", which tells us nothing about where to grow.
    const [heardAbout, setHeardAbout] = useState<HeardAbout | "">("");
    const [heardAboutDetail, setHeardAboutDetail] = useState("");
    const [referrer, setReferrer] = useState<{ id: string; companyName: string } | null>(null);

    const [contactFirst, setContactFirst] = useState("");
    const [contactLast, setContactLast] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [contactRole, setContactRole] = useState("");

    const [siteName, setSiteName] = useState("");
    const [addressLine, setAddressLine] = useState("");
    const [addressLine2, setAddressLine2] = useState("");
    const [city, setCity] = useState("");
    const [region, setRegion] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [waterProviderId, setWaterProviderId] = useState("");
    // Parent water district / city (#330). Held separately from the leaf
    // waterProviderId: deriving the city from the leaf collapsed a multi-PWS
    // city back to "Not sure" the instant the leaf was cleared, so those
    // cities could never be selected and the provider was silently lost.
    const [cityId, setCityId] = useState("");
    const [accountNumber, setAccountNumber] = useState("");

    const [notes, setNotes] = useState("");

    // Pre-fill the primary-contact fields from the call dock handoff
    // (/customers/new?phone=…&name=…). Runs once on mount so manual edits
    // afterward are never clobbered.
    useEffect(() => {
        const phoneParam = searchParams.get("phone");
        const nameParam = searchParams.get("name");
        const companyParam = searchParams.get("company");
        if (phoneParam) setContactPhone((prev) => prev || phoneParam);
        if (companyParam) setCompanyName((prev) => prev || companyParam);
        if (nameParam) {
            const parts = nameParam.trim().split(/\s+/);
            if (parts[0]) setContactFirst((prev) => prev || parts[0]);
            if (parts.length > 1) setContactLast((prev) => prev || parts.slice(1).join(" "));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Server-side error state ───────────────────────────────────────
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
    const [formErrors, setFormErrors] = useState<string[]>([]);
    const [conflicts, setConflicts] = useState<IntakeConflict[]>([]);

    // Clear referrer if the answer ever flips away from Referral — keeping a
    // stale referrer FK around when the cross-field invariant would reject it
    // would just produce a confusing 400 on submit.
    useEffect(() => {
        if (heardAbout !== "referral" && referrer) setReferrer(null);
    }, [heardAbout, referrer]);
    const detailPrompt = heardAboutDetailPrompt(heardAbout);

    // ── Water providers (loaded once for the dropdown) ────────────────
    const { data: waterProviders } = useQuery({
        queryKey: ["water-providers", "intake"],
        queryFn: () => api.get<{ providers: WaterProvider[] }>("/api/water-providers?active=true"),
        staleTime: 5 * 60_000,
    });

    // ── Submit ─────────────────────────────────────────────────────────
    const mutation = useMutation({
        mutationFn: (body: unknown) => api.post<IntakeSuccess>("/api/customers/intake", body),
        onMutate: () => {
            setFieldErrors({});
            setFormErrors([]);
            setConflicts([]);
        },
        onSuccess: (result) => {
            const trimmedName = companyName.trim();
            toast.success(`Created ${trimmedName}. Add a job or quote to get started.`);
            // Hand the user straight to the customer record where the
            // most common next step (start a job, send a quote) lives.
            // The intake handoff state lets the detail page surface the
            // queue-first-test action + similar-customer warning that
            // used to render in the in-page SuccessPanel.
            navigate(`/customers/${result.customer_id}`, {
                state: {
                    intakeHandoff: {
                        siteId: result.site_id,
                        intakeNotes: notes.trim(),
                        similar: result.similar,
                        companyName: trimmedName,
                    },
                },
            });
        },
        onError: (err) => {
            const parsed = parseIntakeError(err);
            switch (parsed.kind) {
                case "duplicate":
                    setConflicts(parsed.conflicts);
                    toast.error("This customer already exists. Open the matching record below.");
                    break;
                case "validation":
                    setFieldErrors(parsed.details.fieldErrors);
                    setFormErrors(parsed.details.formErrors);
                    toast.error("Please fix the highlighted fields and try again.");
                    break;
                case "auth":
                case "network":
                case "unknown":
                    toast.error(parsed.message);
                    break;
            }
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (mutation.isPending) return;

        if (!customerType) {
            setTypeError("Pick Home or Business.");
            toast.error("Pick Home or Business for this customer.");
            return;
        }

        // Mirror the server refine (customerIntake.ts): a customer with no way
        // to reach them can't be invoiced or notified. Surface it inline rather
        // than letting the server bounce a generic form-level error.
        if (!contactEmail.trim() && !contactPhone.trim()) {
            setFieldErrors({ "contact.email": ["Enter an email or phone — at least one is required."] });
            setFormErrors([]);
            toast.error("Add an email or phone for the primary contact.");
            return;
        }

        const contact = blankToUndefined({
            firstName: contactFirst,
            lastName: contactLast,
            email: contactEmail,
            phone: contactPhone,
            roleTitle: contactRole,
        });
        const site = blankToUndefined({
            name: siteName,
            addressLine,
            addressLine2,
            city,
            region,
            postalCode,
            waterProviderId: waterProviderId || undefined,
            accountNumber: accountNumber.trim() || undefined,
        });
        const body = blankToUndefined({
            companyName,
            customerType,
            heardAbout: heardAbout || undefined,
            heardAboutDetail,
            referringCustomerId: referrer?.id,
            notes,
            contact,
            site,
        });

        mutation.mutate(body);
    };

    // ── Render ────────────────────────────────────────────────────────
    return (
        <div className="space-y-6 max-w-4xl mx-auto pb-24">
            <div>
                <BackLink
                    fallback="/customers"
                    fallbackLabel="Back to customers"
                    className="text-sm text-shell-foreground/80 hover:text-shell-foreground inline-flex items-center gap-1 mb-2"
                    icon={<ArrowLeft className="h-3 w-3" />}
                />
                <h1 className="text-2xl font-black tracking-tight text-shell-foreground">New customer intake</h1>
                <p className="text-shell-foreground/80 text-sm mt-1">
                    Capture the company, primary contact, and first service address in one shot.
                </p>
            </div>

            {conflicts.length > 0 && <ConflictBanner conflicts={conflicts} />}
            {formErrors.length > 0 && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                    {formErrors.map((m, i) => (
                        <p key={i}>{m}</p>
                    ))}
                </div>
            )}

            {/* autoComplete=off: customer intake is for ENTERING new
                customer/contact/site records — browser autofill of the
                operator's own saved profile is actively harmful here
                because it produces duplicate or wrong-person entries.
                Firefox/Safari honor this on the form element; Chrome
                mostly ignores it for "name"-typed fields but our
                controlled inputs lack the matching `name=` attributes
                so the heuristic rarely fires anyway. Per
                docs/PRE-CUTOVER-UX-FEEDBACK.md #22. */}
            <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
                {/* ── About this customer ─────────────────────── */}
                <Card>
                    <CardHeader className="flex-row items-center gap-2 space-y-0">
                        <Building2 className="h-4 w-4 text-foreground/80" />
                        <CardTitle className="text-base">About this customer</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                        <Field
                            label="Company / household name"
                            required
                            error={pickError(fieldErrors, "companyName")}
                            className="md:col-span-2"
                        >
                            <Input
                                required
                                autoFocus
                                placeholder="Acme Plumbing LLC"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                            />
                        </Field>

                        <div className="md:col-span-2">
                            <HomeOrBusinessButtons
                                value={customerType}
                                onChange={(v) => {
                                    setCustomerType(v);
                                    setTypeError(null);
                                }}
                                error={typeError ?? pickError(fieldErrors, "customerType") ?? null}
                            />
                        </div>

                        <Field
                            label="How did they hear about us?"
                            required
                            hint="Ask the caller. This is how we learn where new customers come from."
                            error={pickError(fieldErrors, "heardAbout")}
                        >
                            <Select
                                required
                                aria-label="How did they hear about us?"
                                value={heardAbout}
                                onChange={(e) => setHeardAbout(e.target.value as HeardAbout | "")}
                            >
                                <option value="" disabled>
                                    Pick one
                                </option>
                                {HEARD_ABOUT_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        {detailPrompt && (
                            <Field
                                label={detailPrompt.label}
                                error={pickError(fieldErrors, "heardAboutDetail")}
                                className="md:col-span-2"
                            >
                                <Input
                                    aria-label={detailPrompt.label}
                                    placeholder={detailPrompt.placeholder}
                                    value={heardAboutDetail}
                                    onChange={(e) => setHeardAboutDetail(e.target.value)}
                                />
                            </Field>
                        )}

                        {heardAbout === "referral" && (
                            <Field
                                label="Referred by one of our customers? (optional)"
                                hint="If the person who sent them is already a customer, pick them so we can thank them."
                                error={pickError(fieldErrors, "referringCustomerId")}
                                className="md:col-span-2"
                            >
                                <ReferrerPicker value={referrer} onChange={setReferrer} />
                            </Field>
                        )}
                    </CardContent>
                </Card>

                {/* ── Primary contact ─────────────────────────── */}
                <Card>
                    <CardHeader className="flex-row items-center gap-2 space-y-0">
                        <User className="h-4 w-4 text-foreground/80" />
                        <CardTitle className="text-base">Primary contact</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                        <Field label="First name" required error={pickError(fieldErrors, "contact.firstName")}>
                            <Input
                                required
                                placeholder="Jane"
                                value={contactFirst}
                                onChange={(e) => setContactFirst(e.target.value)}
                            />
                        </Field>
                        <Field label="Last name" error={pickError(fieldErrors, "contact.lastName")}>
                            <Input
                                placeholder="Doe"
                                value={contactLast}
                                onChange={(e) => setContactLast(e.target.value)}
                            />
                        </Field>
                        <Field
                            label="Email or phone"
                            required
                            hint="At least one of email or phone is required so the customer can be invoiced and notified."
                            error={pickError(fieldErrors, "contact.email")}
                        >
                            <Input
                                type="email"
                                placeholder="jane@acme.com"
                                value={contactEmail}
                                onChange={(e) => setContactEmail(e.target.value)}
                            />
                        </Field>
                        <Field label="Phone" error={pickError(fieldErrors, "contact.phone")}>
                            <Input
                                type="tel"
                                placeholder="(555) 123-4567"
                                value={contactPhone}
                                onChange={(e) => setContactPhone(e.target.value)}
                                onBlur={(e) => setContactPhone(formatPhoneOnBlur(e.target.value))}
                            />
                        </Field>
                        <Field
                            label="Role / title (optional)"
                            error={pickError(fieldErrors, "contact.roleTitle")}
                            className="md:col-span-2"
                        >
                            <Input
                                placeholder="Property manager, owner, facilities lead..."
                                value={contactRole}
                                onChange={(e) => setContactRole(e.target.value)}
                            />
                        </Field>
                    </CardContent>
                </Card>

                {/* ── Service address ─────────────────────────── */}
                <Card>
                    <CardHeader className="flex-row items-center gap-2 space-y-0">
                        <MapPin className="h-4 w-4 text-foreground/80" />
                        <CardTitle className="text-base">First service address</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-6">
                        <Field
                            label="Site name (optional)"
                            hint="Defaults to the street address if blank."
                            error={pickError(fieldErrors, "site.name")}
                            className="md:col-span-6"
                        >
                            <Input
                                placeholder="Main building, Warehouse #2..."
                                value={siteName}
                                onChange={(e) => setSiteName(e.target.value)}
                            />
                        </Field>
                        <Field
                            label="Street address"
                            required
                            error={pickError(fieldErrors, "site.addressLine")}
                            className="md:col-span-4"
                        >
                            <Input
                                required
                                placeholder="123 Main St"
                                value={addressLine}
                                onChange={(e) => setAddressLine(e.target.value)}
                            />
                        </Field>
                        <Field
                            label="Suite / unit"
                            error={pickError(fieldErrors, "site.addressLine2")}
                            className="md:col-span-2"
                        >
                            <Input
                                placeholder="Suite 200"
                                value={addressLine2}
                                onChange={(e) => setAddressLine2(e.target.value)}
                            />
                        </Field>
                        <Field
                            label="City"
                            required
                            error={pickError(fieldErrors, "site.city")}
                            className="md:col-span-3"
                        >
                            <Input
                                required
                                placeholder="Phoenix"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                            />
                        </Field>
                        <Field
                            label="State"
                            required
                            error={pickError(fieldErrors, "site.region")}
                            className="md:col-span-2"
                        >
                            <Select required value={region} onChange={(e) => setRegion(e.target.value)}>
                                <option value="" disabled>
                                    Select…
                                </option>
                                {US_STATES.map((s) => (
                                    <option key={s.code} value={s.code}>
                                        {s.code}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                        <Field
                            label="ZIP"
                            required
                            error={pickError(fieldErrors, "site.postalCode")}
                            className="md:col-span-1"
                        >
                            <Input
                                required
                                placeholder="85001"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                            />
                        </Field>
                        <Field
                            label="Water provider (optional)"
                            hint="Drives the test fees applied to this site."
                            error={pickError(fieldErrors, "site.waterProviderId")}
                            className="md:col-span-6"
                        >
                            {(() => {
                                const all = waterProviders?.providers ?? [];
                                const provLabel = (p: WaterProvider) => p.display_name?.trim() || p.name;
                                const childrenByParent = new Map<string, WaterProvider[]>();
                                for (const p of all) {
                                    if (p.parent_water_provider_id) {
                                        const arr = childrenByParent.get(p.parent_water_provider_id) ?? [];
                                        arr.push(p);
                                        childrenByParent.set(p.parent_water_provider_id, arr);
                                    }
                                }
                                const kids = cityId ? (childrenByParent.get(cityId) ?? []) : [];
                                const byLabel = (a: WaterProvider, b: WaterProvider) =>
                                    provLabel(a).localeCompare(provLabel(b), undefined, {
                                        sensitivity: "base",
                                    });
                                return (
                                    <>
                                        <Select
                                            value={cityId}
                                            onChange={(e) => {
                                                const id = e.target.value;
                                                setCityId(id);
                                                setAccountNumber("");
                                                const k = childrenByParent.get(id) ?? [];
                                                // Multi-PWS city → keep the city
                                                // selected and wait for a child PWS;
                                                // single-PWS → the city is the leaf.
                                                setWaterProviderId(k.length > 0 ? "" : id);
                                            }}
                                        >
                                            <option value="">— Not sure / look up later —</option>
                                            {all
                                                .filter((p) => !p.parent_water_provider_id)
                                                .slice()
                                                .sort(byLabel)
                                                .map((p) => {
                                                    const hasKids = (childrenByParent.get(p.id) ?? []).length > 0;
                                                    return (
                                                        <option key={p.id} value={p.id}>
                                                            {provLabel(p)}
                                                            {hasKids ? " (multiple public water systems)" : ""}
                                                        </option>
                                                    );
                                                })}
                                        </Select>
                                        {kids.length > 0 && (
                                            <div className="mt-2">
                                                <Select
                                                    value={waterProviderId}
                                                    onChange={(e) => {
                                                        setWaterProviderId(e.target.value);
                                                        setAccountNumber("");
                                                    }}
                                                >
                                                    <option value="">
                                                        — Which public water system (PWS) serves this location? —
                                                    </option>
                                                    {kids
                                                        .slice()
                                                        .sort(byLabel)
                                                        .map((p) => (
                                                            <option key={p.id} value={p.id}>
                                                                {provLabel(p)}
                                                                {p.submission_platform
                                                                    ? ` (${p.submission_platform})`
                                                                    : ""}
                                                            </option>
                                                        ))}
                                                </Select>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </Field>
                        {(() => {
                            const provider = (waterProviders?.providers ?? []).find((p) => p.id === waterProviderId);
                            if (!provider) return null;
                            const isOnline = provider.is_portal === true;
                            if (!isOnline) {
                                return (
                                    <p className="md:col-span-6 text-xs text-foreground/60">
                                        This water provider reports offline — no portal account number needed.
                                    </p>
                                );
                            }
                            const platform = provider.submission_platform?.trim() || null;
                            const portalUrl = provider.portal_url?.trim() || null;
                            const label = platform
                                ? `${platform} account number (optional)`
                                : "Reporting platform account number (optional)";
                            const hint = platform
                                ? `Saves with this address so every future job here reuses it. Ask the customer or look it up in ${platform}.`
                                : "Saves with this address so every future job here reuses it.";
                            return (
                                <Field label={label} hint={hint} className="md:col-span-6">
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="e.g. 12345-A"
                                            value={accountNumber}
                                            onChange={(e) => setAccountNumber(e.target.value)}
                                            className="flex-1"
                                        />
                                        {portalUrl && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="shrink-0"
                                                onClick={() => openExternalLink(portalUrl)}
                                            >
                                                <ExternalLink className="h-4 w-4" />
                                                Open {platform ?? "portal"}
                                            </Button>
                                        )}
                                    </div>
                                </Field>
                            );
                        })()}
                    </CardContent>
                </Card>

                {/* ── Notes ───────────────────────────────────── */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Initial notes / work request</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            rows={4}
                            placeholder="Anything the office should know — what they want done, when they're available, gate codes, prior history mentioned on the call..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                        {pickError(fieldErrors, "notes") && (
                            <p className="text-sm text-destructive mt-1">{pickError(fieldErrors, "notes")}</p>
                        )}
                    </CardContent>
                </Card>

                {/* ── Sticky action bar ───────────────────────── */}
                {/* eslint-disable-next-line no-restricted-syntax -- D1: sticky header — content scrolls beneath it; the blur is load-bearing */}
                <div className="sticky bottom-4 z-10 flex items-center justify-end gap-3 rounded-lg border bg-card/95 p-3 shadow-lg backdrop-blur">
                    <Button type="button" variant="outline" onClick={() => navigate("/customers")}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={mutation.isPending}>
                        {mutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}
                        {mutation.isPending ? "Saving…" : "Create customer"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────

interface FieldProps {
    label: string;
    required?: boolean;
    hint?: string;
    error?: string;
    className?: string;
    children: React.ReactNode;
}

function Field({ label, required, hint, error, className, children }: FieldProps) {
    return (
        <div className={cn("space-y-1.5", className)}>
            <label className="text-sm font-medium">
                {label}
                {required && <span className="text-destructive ml-0.5">*</span>}
            </label>
            {children}
            {error ? (
                <p className="text-xs text-destructive">{error}</p>
            ) : hint ? (
                <p className="text-xs text-foreground/80">{hint}</p>
            ) : null}
        </div>
    );
}

function pickError(map: Record<string, string[]>, key: string): string | undefined {
    return map[key]?.[0];
}

function ConflictBanner({ conflicts }: { conflicts: IntakeConflict[] }) {
    return (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
            <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1 space-y-3">
                    <div>
                        <h2 className="font-semibold text-destructive">This customer already exists</h2>
                        <p className="text-sm text-foreground/80">
                            We block creating duplicates so the office doesn&apos;t end up with two records of the same
                            person. Open the existing customer instead, or change the matching field below and try
                            again.
                        </p>
                    </div>
                    <ul className="space-y-2">
                        {conflicts.map((c, i) => (
                            <li
                                key={`${c.field}-${c.conflict_customer_id}-${i}`}
                                className="rounded border bg-background p-3 text-sm flex items-start justify-between gap-3"
                            >
                                <div>
                                    <div className="font-medium">{c.conflict_company_name}</div>
                                    <div className="text-xs text-foreground/80 mt-0.5">
                                        Matched on <span className="font-mono">{c.field}</span>
                                        {c.detail ? ` — ${c.detail}` : ""}
                                    </div>
                                </div>
                                <Link
                                    to={`/customers/${c.conflict_customer_id}`}
                                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline shrink-0"
                                >
                                    Open <ExternalLink className="h-3 w-3" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────
// Referrer picker — search-as-you-type against /api/customers
// ────────────────────────────────────────────────────────────────────────

interface ReferrerPickerProps {
    value: { id: string; companyName: string } | null;
    onChange: (v: { id: string; companyName: string } | null) => void;
}

function ReferrerPicker({ value, onChange }: ReferrerPickerProps) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const debounced = useDebounce(query, 200);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Close the dropdown on outside click. Avoids covering other fields
    // when the office tabs past it without picking.
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const enabled = !value && debounced.trim().length >= 2;
    const { data, isFetching } = useQuery({
        queryKey: ["customers", "referrer-search", debounced],
        queryFn: () => {
            const p = new URLSearchParams({ q: debounced.trim(), limit: "8" });
            return api.get<{ customers: CustomerSearchRow[] }>(`/api/customers?${p}`);
        },
        enabled,
        staleTime: 30_000,
    });

    const results = useMemo(() => data?.customers ?? [], [data]);

    if (value) {
        return (
            <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
                <Building2 className="h-4 w-4 text-foreground/80" />
                <span className="text-sm font-medium flex-1">{value.companyName}</span>
                <button
                    type="button"
                    aria-label="Clear referring customer"
                    onClick={() => onChange(null)}
                    className="text-foreground/80 hover:text-foreground"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="relative">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/80 pointer-events-none" />
                <Input
                    placeholder="Search existing customers…"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    className="pl-9"
                />
            </div>
            {open && enabled && (
                <div className="absolute z-20 mt-1 w-full rounded-lg border bg-popover shadow-lg max-h-64 overflow-auto">
                    {isFetching && results.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-foreground/80 inline-flex items-center gap-2">
                            <Loader2 className="h-3 w-3 animate-spin" /> Searching…
                        </div>
                    ) : results.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-foreground/80">No matches.</div>
                    ) : (
                        <ul>
                            {results.map((r) => (
                                <li key={r.id}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onChange({ id: r.id, companyName: r.company_name });
                                            setOpen(false);
                                            setQuery("");
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center justify-between gap-2"
                                    >
                                        <span className="truncate">{r.company_name}</span>
                                        {!r.is_active && <span className="text-xs text-foreground/80">inactive</span>}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────
// Hooks
// ────────────────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
}
